import { useEffect, useState } from 'react';
import { NewsClient, NewsItemDto } from '../../../app/services/api/news';

interface UseDashboardNewsResult {
  pinnedItems: NewsItemDto[];
  otherItems: NewsItemDto[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

interface StoreContext {
  storeNumber?: string;
  franchiseId?: number;
}

export function useDashboardNews(storeContext?: StoreContext): UseDashboardNewsResult {
  const [pinnedItems, setPinnedItems] = useState<NewsItemDto[]>([]);
  const [otherItems, setOtherItems] = useState<NewsItemDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshCounter, setRefreshCounter] = useState(0);

  useEffect(() => {
    let cancelled = false;

    async function fetchNews() {
      setLoading(true);
      setError(null);

      try {
        const client = new NewsClient();

        // Fetch published news items for the current store/franchise
        const items = await client.getNewsItems({
          status: 'Published',
          franchiseId: storeContext?.franchiseId,
          storeNumber: storeContext?.storeNumber,
          includeExpired: false,
        });

        if (cancelled) return;

        // Separate pinned and non-pinned items
        const pinned = items.filter((item) => item.isPinned);
        const other = items.filter((item) => !item.isPinned);

        setPinnedItems(pinned);
        setOtherItems(other);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load news');
        setPinnedItems([]);
        setOtherItems([]);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchNews();

    return () => {
      cancelled = true;
    };
  }, [storeContext?.storeNumber, storeContext?.franchiseId, refreshCounter]);

  const refresh = () => {
    setRefreshCounter((c) => c + 1);
  };

  return {
    pinnedItems,
    otherItems,
    loading,
    error,
    refresh,
  };
}
